import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  // --- CONFIGURATION FOR SKINFUEL UPSELLS ---
  #FACE_ID         = 52632926454098;
  #BODY_ID         = 52632900174162;
  #LIPS_ID         = 52104501526866;
  #DUO_ID          = 52340378829138;
  #DUO_BUNDLE_ID   = 52608088932690;

  #FACE_UPSELL_85  = 53244140290386;
  #BODY_UPSELL_85  = 53244143599954;
  #LIPS_UPSELL_53  = 53244148613458;

  #MINI_FACE_ID        = 52104488255826;
  #MINI_BODY_ID        = 52340378927442;
  #MINI_FACE_UPSELL_ID = 53592968069458;
  #MINI_BODY_UPSELL_ID = 53592992383314;

  #ALL_UPSELL_IDS = [53244140290386, 53244143599954, 53244148613458, 53592968069458, 53592992383314];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.addEventListener('click', this.#handleUpsellButtonClick);
    this.#disableUpsellQuantitySelectors();
    this.#validateUpsellItems();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    this.removeEventListener('click', this.#handleUpsellButtonClick);
  }

  /**
   * Evaluates cart contents and safely purges orphaned upsell variants
   */
  #validateUpsellItems() {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const updates = {};
        let needsUpdate = false;

        const hasFace     = cart.items.some((i) => i.variant_id === this.#FACE_ID);
        const hasBody     = cart.items.some((i) => i.variant_id === this.#BODY_ID);
        const hasDuo      = cart.items.some((i) => i.variant_id === this.#DUO_ID || i.variant_id === this.#DUO_BUNDLE_ID);
        const hasMiniFace = cart.items.some((i) => i.variant_id === this.#MINI_FACE_ID);
        const hasMiniBody = cart.items.some((i) => i.variant_id === this.#MINI_BODY_ID);

        const uniqueLines = cart.items.length;

        cart.items.forEach((item) => {
          const vid = item.variant_id;
          
          if (this.#ALL_UPSELL_IDS.includes(vid)) {
            let shouldRemove = false;

            if (uniqueLines > 2) shouldRemove = true;

            if (vid === this.#BODY_UPSELL_85 && !hasFace) shouldRemove = true;
            if (vid === this.#FACE_UPSELL_85 && !hasBody) shouldRemove = true;
            if (vid === this.#LIPS_UPSELL_53 && !hasDuo)  shouldRemove = true;
            if (vid === this.#MINI_BODY_UPSELL_ID && !hasMiniFace) shouldRemove = true;
            if (vid === this.#MINI_FACE_UPSELL_ID && !hasMiniBody) shouldRemove = true;

            if (item.quantity > 1) {
              updates[item.key] = 1;
              needsUpdate = true;
            }

            if (shouldRemove) {
              updates[item.key] = 0;
              needsUpdate = true;
            }
          }
        });

        if (needsUpdate) {
          const sectionsToUpdate = this.#getSectionsToUpdate();
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              updates: updates,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname
            })
          })
          .then((res) => res.json())
          .then((responseJson) => {
            this.dispatchEvent(
              new CartUpdateEvent({}, this.sectionId, {
                itemCount: responseJson.item_count,
                source: 'cart-items-component',
                sections: responseJson.sections,
              })
            );

            if (responseJson.sections?.[this.sectionId]) {
              morphSection(this.sectionId, responseJson.sections[this.sectionId]);
              this.#updateCartQuantitySelectorButtonStates();
              this.#disableUpsellQuantitySelectors(); // Re-apply immediately after removal updates morph
            }
          });
        }
      });
  }

  /**
   * Catches and executes upsell button additions reliably
   * @param {MouseEvent} event 
   */
  #handleUpsellButtonClick = (event) => {
    const btn = event.target.closest('[data-upsell-variant]');
    if (!btn || btn.classList.contains('is-loading')) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = parseInt(btn.dataset.upsellVariant, 10);
    if (!variantId || isNaN(variantId)) return;

    btn.classList.add('is-loading');
    btn.textContent = '...';
    btn.style.pointerEvents = 'none';

    const sectionsToUpdate = this.#getSectionsToUpdate();
    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: { _upsell: 'true' },
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname
    });

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: fetchConfig('json').headers,
      body: body
    })
    .then((response) => response.json())
    .then((responseJson) => {
      if (responseJson.errors) throw new Error(responseJson.errors);
      btn.textContent = '✓ Tillagd';

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: responseJson.item_count,
          source: 'cart-items-component',
          sections: responseJson.sections,
        })
      );

      if (responseJson.sections?.[this.sectionId]) {
        morphSection(this.sectionId, responseJson.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
        this.#disableUpsellQuantitySelectors(); // Re-apply immediately after addition morph
      }
    })
    .catch((error) => {
      console.error('Upsell Framework Error:', error);
      btn.textContent = 'Lägg till';
      btn.style.pointerEvents = 'auto';
    })
    .finally(() => {
      btn.classList.remove('is-loading');
    });
  };

  /**
   * Disables quantity input selectors specifically for active promo/upsell variant matches
   */
  #disableUpsellQuantitySelectors() {
    // Run an instant pass
    const runDisable = () => {
      const selectors = this.querySelectorAll('cart-quantity-selector-component, .quantity-selector');
      selectors.forEach((selector) => {
        const variantId = parseInt(selector.dataset.variantId || selector.getAttribute('data-id'), 10);
        if (this.#ALL_UPSELL_IDS.includes(variantId)) {
          selector.style.visibility = 'hidden';
          selector.style.pointerEvents = 'none';
        }
      });
    };

    runDisable();
    // Use a secondary timeout catch-all block to prevent flash of content during framework morphing delays
    setTimeout(runDisable, 50);
    setTimeout(runDisable, 200);
  }

  /**
   * Helper to fetch active sectional areas on current viewport layout
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        this.#disableUpsellQuantitySelectors(); // Runs immediately when the change action resolves
        this.#validateUpsellItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
    
    this.#disableUpsellQuantitySelectors(); // Clear quantity inputs instantly on mutation notices
    this.#validateUpsellItems();
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}